import { Component, inject, Inject, signal, ViewChild } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AbbModalComponent } from '@components/abb-modal/abb-modal.component';
import { MODAL_DATA, ModalData } from '@components/abb-modal/modal';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { Apollo } from 'apollo-angular';
import { CognitoService } from '@services/cognito/cognito.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import Validation from '@utils/validation';
import { ToastService } from '@services/ui/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { getErrorMessage } from '@utils/errors';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { t } from '@utils/strings';
import { ResetPasswordMutation } from 'gql/graphql';
import { resetPassword } from '@graphql/user';

@Component({
  selector: 'abb-change-psw-modal',
  standalone: true,
  template: `
    <abb-modal [title]="'Change Password' | t" #modalRef>
      <form
        id="change-psw-form"
        [formGroup]="changePswForm"
        (ngSubmit)="onSubmit()"
        class="w-full h-full flex flex-col justify-between pt-2"
      >
        <div class="w-full flex flex-col gap-2 relative">
          <mat-form-field class="w-full" color="primary">
            <mat-label>{{ 'Old password' | t }}</mat-label>
            <input
              matInput
              formControlName="oldPassword"
              name="oldPassword"
              [type]="hideOldPsw() ? 'password' : 'text'"
            />
            <abb-icon
              matSuffix
              (click)="hideOldPsw.set(!hideOldPsw())"
              class="cursor-pointer pr-4 text-abb-font/85"
              [name]="hideOldPsw() ? 'mat/visibility_off' : 'mat/visibility'"
            />
            @if(changePswForm.get('oldPassword')?.hasError('required')){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            } @if(changePswForm.get('oldPassword')?.hasError('minlength')){
            <mat-error>{{
              'Password must be at least 8 characters long' | t
            }}</mat-error>
            } @if(changePswForm.get('oldPassword')?.hasError('maxlength')){
            <mat-error>{{
              'Password must not exceed 256 characters' | t
            }}</mat-error>
            }
          </mat-form-field>
          <mat-form-field class="w-full" color="primary">
            <mat-label>{{ 'New password' | t }}</mat-label>
            <input
              matInput
              formControlName="newPassword"
              name="newPassword"
              [type]="hidePsw() ? 'password' : 'text'"
            />
            <abb-icon
              matSuffix
              (click)="hidePsw.set(!hidePsw())"
              class="cursor-pointer pr-4 text-abb-font/85"
              [name]="hidePsw() ? 'mat/visibility_off' : 'mat/visibility'"
            />
            @if(changePswForm.get('newPassword')?.hasError('required')){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            } @if(changePswForm.get('newPassword')?.hasError('minlength')){
            <mat-error>{{
              'Password must be at least 8 characters long' | t
            }}</mat-error>
            } @if(changePswForm.get('newPassword')?.hasError('maxlength')){
            <mat-error>{{
              'Password must not exceed 256 characters' | t
            }}</mat-error>
            <!-- prettier-ignore -->
            } @else if(changePswForm.get('newPassword')?.hasError('newPassword')){
            <mat-error class="truncate" overflowTooltip>{{
              'Password must include uppercase and lowercase letters, a number, and a special character (e.g., !@#$%^&*())'
                | t
            }}</mat-error>
            }
          </mat-form-field>
          <mat-form-field class="w-full" color="primary">
            <mat-label>{{ 'Confirm new password' | t }}</mat-label>
            <input
              matInput
              formControlName="confirmNewPassword"
              name="confirmNewPassword"
              [type]="hideCfrPsw() ? 'password' : 'text'"
            />
            <abb-icon
              matSuffix
              (click)="hideCfrPsw.set(!hideCfrPsw())"
              class="cursor-pointer pr-4 text-abb-font/85"
              [name]="hideCfrPsw() ? 'mat/visibility_off' : 'mat/visibility'"
            />
            @if(changePswForm.get('confirmNewPassword')?.hasError('required')){
            <mat-error>{{ 'This field is required' | t }}</mat-error>
            } @if
            (changePswForm.get('confirmNewPassword')?.hasError('matching')) {
            <mat-error>{{
              'Confirm new password does not match' | t
            }}</mat-error>
            }
          </mat-form-field>
        </div>
      </form>
      <div footer class="w-full flex flex-row gap-8 justify-end">
        <abb-button
          variant="clean"
          [disabled]="loading()"
          (onclick)="onCancel()"
          >{{ 'cancel' | t }}</abb-button
        >
        <abb-button
          class="w-32"
          type="submit"
          form="change-psw-form"
          [disabled]="!changePswForm.valid || loading()"
          [loading]="loading()"
        >
          {{ 'Save' | t }}</abb-button
        >
      </div>
    </abb-modal>
  `,
  styles: ``,
  imports: [
    AbbModalComponent,
    TPipe,
    AbbButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    OverflowTooltipDirective,
    AbbIconComponent,
  ],
  providers: [
    { provide: TRANSLATION_PREFIX, useValue: 'settings.tabs.account' },
  ],
})
export class ChangePswModalComponent implements ModalData {
  @ViewChild('modalRef') modalRef: AbbModalComponent | undefined;
  private prefix = inject(TRANSLATION_PREFIX);
  loading = signal(false);
  hideOldPsw = signal(true);
  hidePsw = signal(true);
  hideCfrPsw = signal(true);
  changePswForm = new FormGroup(
    {
      oldPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(256),
      ]),
      newPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(256),
        Validation.field('newPassword'),
      ]),
      confirmNewPassword: new FormControl('', [Validators.required]),
    },
    Validation.match('newPassword', 'confirmNewPassword')
  );

  constructor(
    @Inject(MODAL_DATA) public data: undefined,
    public apollo: Apollo,
    private cognitoService: CognitoService,
    private toast: ToastService,
    private translate: TranslateService
  ) {
    this.loading.set(false);
  }

  onCancel() {
    this.modalRef?.dispose();
  }

  onSubmit(): void {
    if (this.changePswForm.invalid) {
      return;
    }
    this.loading.set(true);

    this.apollo
      .mutate<ResetPasswordMutation>({
        mutation: resetPassword,
      })
      .subscribe({
        next: () => {
          this.cognitoService.changePassword({
            oldPassword: this.changePswForm.value.oldPassword!,
            newPassword: this.changePswForm.value.newPassword!,
          });
        },
        error: (err) => {
          this.loading.set(false);
          /* this.toast.notifyError(
          t(this.prefix,
            `An error occurred while updating password`, this.translate
          )
        ); */
          console.error('An error occurred while updating password:', err);
          let errMsg = getErrorMessage(err, this.translate);
          this.toast.notifyError(errMsg);
        },
      });
    this.modalRef?.handleClose();
    this.loading.set(false);
    this.toast.notifySuccess(
      t(this.prefix, `Password updated successfully`, this.translate)
    );
  }
}
